<template>
    <div>
        <aside v-if="filters.length > 0" class="fui-i w-full to_md:mb-16 md:w-56 xl:w-64 md:mr-16">
            <slot name="filter" :filter="filter"></slot>
        </aside>

        <section class="block md:flex-1">
            <div v-if="filters.length > 0" class="lg:flex justify-between items-center mb-8">
                <p v-if="query" class="to_lg:mb-2">{{ count }} products found for "{{ query }}".</p>
                <p v-else class="to_lg:mb-2">Showing {{ offsetStart + 1 }} to {{ offsetEnd }} of {{ $apollo.queries.products.loading ? '…' : count }} products</p>

                <div>
                    <LabeledDropdown
                        v-model="orderBy"
                        label="Sort by"
                        name="orderBy"
                        class="px-0"
                        :required="false"
                        :options="[
                            { label: 'Alphabetically (A-Z)', value: 'title ASC' },
                            { label: 'Alphabetically (Z-A)', value: 'title DESC' },
                            { label: 'Price (Low to high)', value: 'defaultPrice ASC' },
                            { label: 'Price (High to low)', value: 'defaultPrice DESC' },
                        ]"
                    />
                </div>
            </div>

            <div class="shop-products">
                <ShopProduct
                    v-for="product in products"
                    :key="product.id"
                    class="shop-products__item"
                    :product="product.element ? product.element : product"
                />
            </div>

            <p v-if="!$apollo.queries.products.loading && products.length === 0">
                Sorry, no products could be found for the {{ query ? 'entered query' : 'selected options' }}.
            </p>

            <Pagination
                :items="count"
                :items-per-page="24"
                :current="page"
                @changed="pageChanged"
            />
        </section>
    </div>
</template>

<script>
import { PRODUCTS } from '@/graphql/products';
import qs from 'qs';
import LabeledDropdown from './LabeledDropdown.vue';
import Pagination from './Pagination.vue';
import ShopProduct from './ShopProduct.vue';

export default {
    components: {
        ShopProduct,
        Pagination,
        LabeledDropdown,
    },

    props: {
        filters: {
            type: Array,
            default: () => { return ([]); },
        },
    },

    apollo: {
        products: {
            query: PRODUCTS,
            variables() {
                return this.getVariables();
            },
            update(data) {
                this.count = data.count;
                return data.products;
            },
        },
    },

    data() {
        const filter = {};

        for (const shopFilter of this.filters) {
            filter[shopFilter.field] = typeof shopFilter.default !== 'undefined' ? shopFilter.default : [];
        }

        const params = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        let orderBy = 'title ASC';
        if (typeof params.orderBy !== 'undefined') {
            ({ orderBy } = params);
            delete params.orderBy;
        }

        let query = null;

        if (typeof params.q !== 'undefined') {
            query = params.q;
            delete params.q;
        }

        for (const key in params) {
            if (Object.prototype.hasOwnProperty.call(params, key)) {
                const value = params[key];

                if (typeof filter[key] !== 'undefined') {
                    filter[key] = value;
                }
            }
        }

        return {
            count: 0,
            page: 1,
            products: [],
            orderBy,
            query,
            filter,
        };
    },

    computed: {
        offsetStart() {
            return 24 * (this.page - 1);
        },
        offsetEnd() {
            return Math.min(this.count, this.offsetStart + 24);
        },
    },

    watch: {
        filter: {
            deep: true,
            handler() {
                this.page = 1;
            },
        },
    },

    methods: {
        getVariables() {
            const variables = {
                limit: 24,
                offset: 24 * (this.page - 1),
                search: this.query,
                orderBy: this.orderBy,
            };

            const params = {
                orderBy: this.orderBy,
            };

            if (this.query) {
                params.q = this.query;
            }

            for (const key in this.filter) {
                if (Object.prototype.hasOwnProperty.call(this.filter, key)) {
                    const value = this.filter[key];

                    if (value.length > 0) {
                        if (key === 'price') {
                            variables.price = [
                                'and',
                                `>= ${value[0]}`,
                                `<= ${value[1]}`,
                            ];
                        } else {
                            variables[key] = value;
                        }

                        params[key] = value;
                    }
                }
            }

            history.replaceState(null, '', `?${qs.stringify(params, {
                encodeValuesOnly: true,
                arrayFormat: 'brackets',
            })}`);

            return variables;
        },

        pageChanged(page) {
            this.page = page;

            this.$nextTick(() => {
                this.$el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'start',
                });
            });
        },
    },
};
</script>
